import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Globe, 
  Eye, 
  Calendar, 
  MapPin, 
  User, 
  Hash, 
  FileText,
  Search,
  Filter,
  ExternalLink,
  Info,
  X,
  Scale
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PublicNoticesPage = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [noticeTypes, setNoticeTypes] = useState([]);
  const [showDisclosureModal, setShowDisclosureModal] = useState(true);

  // Fetch public notices
  const fetchPublicNotices = async () => {
    try {
      setLoading(true);
      const typeParam = selectedType !== 'all' ? `&notice_type=${encodeURIComponent(selectedType)}` : '';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notices/public?limit=50${typeParam}`);

      if (response.ok) {
        const notices = await response.json();
        setNotices(notices);
      }
    } catch (error) {
      console.error('Error fetching public notices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch notice types for filtering
  const fetchNoticeTypes = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notices/config/notice-types`);
      if (response.ok) {
        const types = await response.json();
        setNoticeTypes(types);
      }
    } catch (error) {
      console.error('Error fetching notice types:', error);
    }
  };

  useEffect(() => {
    fetchPublicNotices();
    fetchNoticeTypes();
  }, [selectedType]);

  // Filter notices based on search term
  const filteredNotices = notices.filter(notice =>
    notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    notice.notice_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (notice.user_name && notice.user_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getNoticeTypeIcon = (type) => {
    switch (type) {
      case 'Notice of Conditional Acceptance (Respondent has X days to rebut under penalty of default)':
        return <FileText className="w-4 h-4" />;
      case 'Notice of Default & Opportunity to Cure':
        return <FileText className="w-4 h-4" />;
      case 'Notice of Dishonor (Non-Response / Incomplete / Refused for Cause)':
        return <FileText className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const handleViewNotice = (noticeId) => {
    // Navigate to individual notice page
    window.location.href = `/public-notices/${noticeId}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Public Notice Disclosure Modal */}
      {showDisclosureModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-center space-x-3">
                <Scale className="w-8 h-8 text-blue-600" />
                <div>
                  <h2 className="text-2xl font-bold text-blue-900">PUBLIC NOTICE DISCLOSURE</h2>
                  <p className="text-sm text-blue-700">Important Legal Information</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDisclosureModal(false)}
                className="hover:bg-white/50"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>
            
            <div className="p-8 overflow-y-auto max-h-96">
              <div className="space-y-6 text-gray-700">
                <p className="text-lg leading-relaxed">
                  This Notice is hereby published by the undersigned in the capacity of a <strong>Private Living Man or Woman</strong>, 
                  acting outside of any commercial, corporate, or statutory jurisdiction, and not as a surety, agent, or representative 
                  of any fiction or entity. This Notice is made publicly available on the TheEqualizer.AI platform for the purpose 
                  of establishing lawful notice to all parties, under the principles of:
                </p>
                
                <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
                  <p className="font-semibold text-blue-900">
                    • Due Process of Law<br/>
                    • Good Faith and Fair Dealing<br/>
                    • The Right to Petition for Redress<br/>
                    • The Doctrine of Estoppel by Silence
                  </p>
                </div>

                <p className="text-lg leading-relaxed">
                  All declarations, rebuttals, and affirmations posted herein are executed under <strong>Common Law</strong>, 
                  <strong> Equity Jurisprudence</strong>, and where applicable, under the protections of Natural, Constitutional, 
                  and International Law. Unless otherwise rebutted in kind, with verified evidence and within the timeframe 
                  prescribed (if stated), all contents of this Notice shall stand as:
                </p>

                <div className="text-center bg-amber-50 rounded-lg p-6 border-2 border-amber-200">
                  <p className="text-xl font-bold text-amber-800">
                    True, Correct, Complete, and Binding upon the parties to whom it concerns
                  </p>
                </div>

                <p className="text-lg font-medium text-center text-green-700">
                  This Notice is not a threat, it is a peaceful act of lawful record and public affirmation.
                </p>
              </div>
            </div>
            
            <div className="p-6 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Info className="w-4 h-4" />
                <span>This disclosure applies to all public notices on this platform</span>
              </div>
              <Button
                onClick={() => setShowDisclosureModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2"
              >
                I Understand
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 pb-8 pt-24">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Info className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">Public Notice Disclosure</p>
                <p className="text-xs text-blue-700">All notices are subject to legal disclosure requirements</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDisclosureModal(true)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
            >
              View Disclosure
            </Button>
          </div>
        </div>
        {/* Features Section */}
        <div className="mb-12 bg-white rounded-xl shadow-sm border p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Public Notice Board Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Lawful Documentation</h3>
              <p className="text-gray-600 text-sm">
                Create legally binding notices with cryptographic proof and timestamp verification for sovereign authority.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Global Publication</h3>
              <p className="text-gray-600 text-sm">
                Publish notices to the world with permanent record keeping and accessible public viewing for all parties.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Hash className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Sovereign Proof</h3>
              <p className="text-gray-600 text-sm">
                Each notice includes cryptographic hash verification and sovereign timestamp for irrefutable legal standing.
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="mb-12 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">{notices.length}+</div>
              <div className="text-sm text-gray-600">Public Notices</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">
                {notices.reduce((sum, notice) => sum + (notice.view_count || 0), 0)}+
              </div>
              <div className="text-sm text-gray-600">Total Views</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">100%</div>
              <div className="text-sm text-gray-600">Verification Rate</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-indigo-600 mb-2">24/7</div>
              <div className="text-sm text-gray-600">Public Access</div>
            </div>
          </div>
        </div>
        <div className="text-center mb-12">
          <div className="mb-6">
            <Globe className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Public Notice Board
            </h1>
            <p className="text-xl text-gray-600">
              "Put the World on Notice — Digitally, Lawfully, Sovereignly."
            </p>
          </div>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            Browse public notices and declarations from the sovereign community. All notices are 
            timestamped with cryptographic proof for verification.
          </p>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search notices by title, type, or author..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Filter by Type */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
              >
                <option value="all">All Notice Types</option>
                {noticeTypes.map((type) => (
                  <option key={type} value={type}>
                    {type.length > 50 ? `${type.substring(0, 50)}...` : type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredNotices.length} public notices
          </div>
        </div>

        {/* Notices Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading public notices...</p>
          </div>
        ) : filteredNotices.length === 0 ? (
          <div className="text-center py-12">
            <Globe className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No notices found</h3>
            <p className="text-gray-500">
              {searchTerm || selectedType !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'No public notices have been published yet'
              }
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredNotices.map((notice) => (
              <Card key={notice.id} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      {getNoticeTypeIcon(notice.notice_type)}
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-lg text-gray-900 line-clamp-2">
                          {notice.title}
                        </CardTitle>
                        <div className="mt-2">
                          <Badge variant="outline" className="text-xs">
                            {notice.notice_type.length > 30 
                              ? `${notice.notice_type.substring(0, 30)}...` 
                              : notice.notice_type
                            }
                          </Badge>
                          {notice.is_featured && (
                            <Badge className="bg-yellow-100 text-yellow-700 text-xs ml-2">
                              Featured
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  {/* Notice Preview */}
                  {notice.body_text && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                      {notice.body_text.substring(0, 150)}...
                    </p>
                  )}
                  
                  {notice.pdf_upload && (
                    <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-center">
                      <FileText className="w-4 h-4 inline text-blue-600 mr-1" />
                      <span className="text-sm text-blue-700">PDF Document Attached</span>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>{new Date(notice.created_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Eye className="w-3 h-3" />
                        <span>{notice.view_count}</span>
                      </div>
                    </div>
                  </div>

                  {/* Author and Location */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                    {notice.user_name && (
                      <div className="flex items-center space-x-1">
                        <User className="w-3 h-3" />
                        <span>{notice.user_name}</span>
                      </div>
                    )}
                    {notice.location && (
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-3 h-3" />
                        <span>{notice.location}</span>
                      </div>
                    )}
                  </div>

                  {/* Hash Proof */}
                  {notice.hash_proof && (
                    <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded">
                      <div className="flex items-center space-x-1 text-xs text-gray-600">
                        <Hash className="w-3 h-3" />
                        <span className="font-mono">{notice.hash_proof.substring(0, 16)}...</span>
                        <span className="text-gray-500">Sovereign Proof</span>
                      </div>
                    </div>
                  )}

                  {/* View Button */}
                  <Button
                    onClick={() => handleViewNotice(notice.id)}
                    className="w-full flex items-center justify-center space-x-2"
                    variant="outline"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>View Full Notice</span>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center bg-white rounded-lg shadow-sm border p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Want to Publish Your Own Notice?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Join TheEqualizer.AI community to publish your own public notices with cryptographic 
            proof and sovereign authority. Choose from multiple subscription plans to fit your needs.
          </p>
          <div className="flex justify-center">
            <Button 
              onClick={() => window.location.href = '/auth'}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Sign Up Now
            </Button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PublicNoticesPage;